import { makeAutoObservable } from 'mobx';
import { ChangeEvent } from 'react';
import { parse } from 'papaparse';
import { z, ZodError } from 'zod';
import { PromoCodesState } from 'src/domains/casino/webview/bonus-engine/campaign/promo-code/PromoCodes.state';
import { PromoCodeViewType } from 'src/api_openapi/v2/bonus-engine/schemas';
import { ToasterState } from 'src/AppState/ToasterState';
import { Common } from 'src/AppState/Common';

class CSVError {
    public constructor(public message: string) {}
}

const PromoCodesCSVSchema = z.array(
    z
        .object({
            promo_code: z.union([z.string(), z.undefined()]),
            use_limit: z.union([z.string(), z.undefined()]).transform((val) => {
                if (val === undefined) {
                    return null;
                }
                const regex = /^\d+$/;
                const parsed = parseInt(val, 10);
                if (regex.test(val) === false || isNaN(parsed)) {
                    throw new CSVError('Column "use_limit" can have only number values.');
                }
                return parsed;
            }),
        })
        .strict()
);

type PromoCodesCSVType = z.TypeOf<typeof PromoCodesCSVSchema>;

export class UploadCSVButtonState {
    private inputRef: HTMLInputElement | null = null;

    public constructor(
        private common: Common,
        private promoCodesState: PromoCodesState
    ) {
        makeAutoObservable(this);
    }

    public setInputRef = (inputRef: HTMLInputElement): void => {
        this.inputRef = inputRef;
    };

    public handleClick = (): void => {
        this.inputRef?.click();
    };

    public handleFileUpload = (event: ChangeEvent<HTMLInputElement>): void => {
        const file = event.target.files?.item(0) ?? null;
        if (file !== null) {
            this.parseCSV(file);
        }
        event.target.value = '';
    };

    private parseCSV = (file: File): void => {
        parse(file, {
            complete: (fileResult) => {
                try {
                    const result = PromoCodesCSVSchema.parse(fileResult.data);
                    const promoCodesView = this.formatPromoCodesFormat(result);
                    this.promoCodesState.addPromoCodesFromCSV(promoCodesView);

                    ToasterState.get(this.common).setToast({
                        variant: 'success',
                        title: 'Success!',
                        message: `Promo codes from "${file.name}" file have been successfully uploaded.`,
                    });
                } catch (error) {
                    const message = this.getErrorMessage(error);

                    ToasterState.get(this.common).setToast({
                        variant: 'error',
                        title: 'Error!',
                        message,
                    });
                }
            },
            header: true,
            skipEmptyLines: true,
        });
    };

    private formatPromoCodesFormat = (promoCodes: PromoCodesCSVType): Array<PromoCodeViewType> => {
        return promoCodes.map((code) => ({
            promoCode: code.promo_code ?? '',
            isPaused: false,
            numberOfUses: null,
            useLimit: code.use_limit,
        }));
    };

    private getErrorMessage = (error: unknown): string => {
        let message = `Cannot upload CSV file. Please check if data format contains following columns: promo_code, use_limit.`;

        if (error instanceof ZodError) {
            if (error.errors[0]?.code === 'unrecognized_keys') {
                const invalidColumns = error.errors[0]?.keys.reduce((acc, item) => `${acc}"${item}" `);
                message = `${message} The file contains invalid columns: ${invalidColumns}`;
            } else {
                message = `${message} ${error.errors[0]?.message}`;
            }
        }

        if (error instanceof CSVError) {
            message = error.message;
        }

        return message;
    };
}
